/* Agent computer panel shell (rules 30, 31; spec 6.6; Browser / Terminal / Files / Diff after the
   OpenHands Agent Canvas, MIT). Stop and Take over stay visible; Pause, New window, Files in "⋯".
   The feature supplies the live views; this shell owns the frame, state colors, caption and replay. */
import type { ReactNode } from 'react';
import { Bot, Download, ExternalLink, Hand, Pause, Play, Square, Undo2 } from 'lucide-react';
import { Button, Chip } from './basics';
import { Menu } from './Menu';
import { Tabs } from './Tabs';

export type ComputerState = 'idle' | 'booting' | 'running' | 'paused' | 'waiting' | 'user' | 'finished' | 'error';

export interface AgentComputerPanelProps {
  state: ComputerState;
  views: { id: string; label: string; content: ReactNode }[];
  view: string;
  onViewChange: (id: string) => void;
  caption?: ReactNode;
  meta?: ReactNode;
  replay?: { step: number; total: number; onChange: (step: number) => void };
  onStop?: () => void;
  onTakeover?: () => void;
  onHandBack?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onNewWindow?: () => void;
  onFiles?: () => void;
}

export function AgentComputerPanel(p: AgentComputerPanelProps) {
  const user = p.state === 'user';
  const current = p.views.find((v) => v.id === p.view) ?? p.views[0];
  return (
    <section className="comp" aria-label="Agent's computer" data-state={p.state} data-control={user ? 'user' : 'agent'} data-cap="AGENT-02">
      <div className="comp-hd">
        <b>Agent's computer</b>
        {p.meta && <Chip>{p.meta}</Chip>}
        <span className="sp" />
        <Tabs bare label="Computer views" value={current?.id ?? ''} onChange={p.onViewChange} tabs={p.views.map((v) => ({ id: v.id, label: v.label }))} />
      </div>
      <div className="comp-view">
        <span className="comp-flag" role="status">
          {user ? <><Hand size={14} aria-hidden="true" /> You are in control</>
            : p.state === 'booting' ? 'Starting computer…'
            : p.state === 'paused' ? 'Paused'
            : p.state === 'waiting' ? 'Waiting for your approval'
            : p.state === 'finished' ? 'Finished'
            : p.state === 'error' ? 'Stopped by an error'
            : <><Bot size={14} aria-hidden="true" /> Agent is acting</>}
        </span>
        <div className="comp-content">{current?.content}</div>
      </div>
      <div className="comp-cap">{user ? 'The agent waits until you hand back. Sign-ins and payments stay with you.' : p.caption}</div>
      {p.replay && (
        <div className="scrub" data-cap="AGENT-17">
          <label htmlFor="cx-replay">Replay</label>
          <input id="cx-replay" type="range" min={1} max={p.replay.total} value={p.replay.step} onChange={(e) => p.replay!.onChange(Number(e.target.value))} />
          <span>Step {p.replay.step} of {p.replay.total}</span>
        </div>
      )}
      <div className="row">
        <Button size="sm" variant="danger" icon={<Square size={14} aria-hidden="true" />} onClick={p.onStop} disabled={p.state === 'finished'}>Stop</Button>
        <Button size="sm" variant={user ? 'primary' : 'secondary'} icon={user ? <Undo2 size={14} aria-hidden="true" /> : <Hand size={14} aria-hidden="true" />} onClick={user ? p.onHandBack : p.onTakeover}>
          {user ? 'Hand back' : 'Take over'}
        </Button>
        <span className="sp" />
        <Menu
          label="More computer actions"
          groups={[{
            label: 'Computer',
            items: [
              p.state === 'paused'
                ? { id: 'resume', label: 'Resume', icon: Play, onSelect: p.onResume }
                : { id: 'pause', label: 'Pause', icon: Pause, onSelect: p.onPause },
              { id: 'window', label: 'Open in new window', icon: ExternalLink, onSelect: p.onNewWindow },
              { id: 'files', label: 'Files the agent made', icon: Download, onSelect: p.onFiles },
            ],
          }]}
        />
      </div>
    </section>
  );
}
